import { FC, useState, useRef, useEffect, useLayoutEffect } from 'react';
import classNames from 'classnames';

import { toast } from 'react-toastify';

import { CopyLinkIcon } from '../../icons/copy-link-icon';
import { ChevronUpDownIcon } from '../../icons/chevron-up-down';
import { HideFromListIcon } from '../../icons/hide-from-list-icon';
import { EllipsisHorizontalIcon } from '../../icons/ellipsis-horizontal';
import { EllipsisHorizontalActiveIcon } from '../../icons/ellipsis-horizontal-active';
import { ConfirmDialog, ActionMenuPopup, CloseButton, IconButton, Heading, Button } from '../../../shared/ui';

export interface AwaitingParticipant {
  id: string;
  displayName: string;
  avatarColor?: string;
  /** Вход разрешён, но участник ещё не подключился — ждём его появления в комнате */
  isAllowedPending?: boolean;
}

export interface OnMeetingParticipant {
  id: string;
  displayName: string;
  avatarColor?: string;
  isPsychologist?: boolean;
}

export interface GroupParticipantsPanelProps {
  onClose: () => void;
  meetLink: string;
  /** Количество участников, ожидающих подключение (открыли ссылку, но ещё не нажали «Подключиться») */
  awaitingCount?: number;
  awaitingParticipants?: AwaitingParticipant[];
  /** Количество участников уже на встрече (нажали вход и в комнате) */
  onMeetingCount?: number;
  onMeetingParticipants?: OnMeetingParticipant[];
  onAllow?: (id: string) => void;
  /** Скрыть участника из списка «Ожидают подключения» (только UI, участнику ничего не отправляется). */
  onHideFromAwaiting?: (id: string) => void;
  onAllowAll?: () => void;
  onMuteAll?: () => void;
  /** Действия по участнику на встрече (из меню «⋯»). Промис резолвится в true только если участник подтвердил (ack) реальное выполнение, false — если не подтвердил за таймаут. */
  onMuteParticipant?: (id: string) => Promise<boolean>;
  onStopVideoParticipant?: (id: string) => Promise<boolean>;
  onMakeCoOrganizer?: (id: string) => void;
  /** Показывать пункт «Назначить соорганизатором» (только психолог, не соорганизатор) */
  canAssignCoOrganizer?: boolean;
  onRemoveParticipant?: (id: string) => void;
}

export const GroupParticipantsPanel: FC<GroupParticipantsPanelProps> = ({
  onClose,
  meetLink,
  awaitingCount,
  awaitingParticipants = [],
  onMeetingCount,
  onMeetingParticipants = [],
  onAllow,
  onHideFromAwaiting,
  onAllowAll,
  onMuteAll,
  onMuteParticipant,
  onStopVideoParticipant,
  onMakeCoOrganizer,
  canAssignCoOrganizer = false,
  onRemoveParticipant,
}) => {
  const displayAwaitingCount = awaitingCount ?? awaitingParticipants.length;
  const displayOnMeetingCount = onMeetingCount ?? onMeetingParticipants.length;
  const [isAwaitingListExpanded, setIsAwaitingListExpanded] = useState(true);
  const [isOnMeetingListExpanded, setIsOnMeetingListExpanded] = useState(true);
  const [openParticipantMenuId, setOpenParticipantMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ bottom: number; right: number } | null>(null);
  const [participantIdToRemove, setParticipantIdToRemove] = useState<string | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (openParticipantMenuId === null) {
      setMenuPosition(null);
      return;
    }
    const el = menuTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Меню показываем сверху от кнопки (отступ 4px)
    setMenuPosition({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
  }, [openParticipantMenuId]);

  useEffect(() => {
    if (openParticipantMenuId === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuTriggerRef.current?.contains(target) || menuContentRef.current?.contains(target)) return;
      setOpenParticipantMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openParticipantMenuId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(meetLink).then(
      () => toast.success('Скопировано'),
      () => toast.error('Не удалось скопировать'),
    );
  };

  const moderationFeedbackToast = (message: string, variant: 'success' | 'warning' = 'success') => {
    toast[variant](message, { autoClose: 2800, position: 'top-center' });
  };

  return (
    <div
      className={classNames(
        'w-full h-full min-h-0 flex flex-col bg-ink-muted rounded-card shadow-overlay overflow-hidden z-[200]',
      )}
      role="dialog"
      aria-label="Участники группы"
    >
      <div className="flex flex-col flex-1 min-h-0 px-6 pt-4 pb-5 gap-5">
        {/* Заголовок */}
        <div className="flex items-center justify-between gap-4 flex-shrink-0 relative z-10">
          <Heading size="lg" tone="inverse">
            Участники
          </Heading>
          <CloseButton tone="inverse" onClick={onClose} />
        </div>

        {/* Поделиться ссылкой — одна строка: подпись, ссылка, кнопка копирования */}
        <div className="flex items-center gap-2 text-white py-2 flex-shrink-0 min-w-0 relative z-10">
          <span className="text-body-l-strong whitespace-nowrap">Поделиться ссылкой</span>
          <span className="text-body-l truncate min-w-0 flex-1 text-white/95">{meetLink}</span>
          <IconButton
            label="Копировать ссылку"
            variant="ghostInverse"
            className="flex-shrink-0 m-0.5"
            onClick={handleCopyLink}
          >
            <CopyLinkIcon className="w-5 h-5" stroke="white" />
          </IconButton>
        </div>

        {/* Ожидают подключения + На встрече: между секциями 32px */}
        <div className="flex flex-col flex-1 min-h-0 gap-[32px]">
          {/* Ожидают подключения */}
          <div className="flex flex-col gap-4 flex-shrink-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 flex-shrink-0 relative z-10">
              <span className="text-white text-heading-lg">Ожидают подключения</span>
              <div className="flex items-center gap-1">
                <span className="text-white text-body-l tabular-nums">{displayAwaitingCount}</span>
                <IconButton
                  label={isAwaitingListExpanded ? 'Свернуть список' : 'Развернуть список'}
                  variant="ghostInverse"
                  aria-expanded={isAwaitingListExpanded}
                  onClick={() => setIsAwaitingListExpanded((v) => !v)}
                >
                  <ChevronUpDownIcon direction={isAwaitingListExpanded ? 'up' : 'down'} />
                </IconButton>
              </div>
            </div>
            {isAwaitingListExpanded && (
              <>
                <div className="flex flex-col gap-4 overflow-y-auto min-h-0 max-h-[220px] isolate">
                  {awaitingParticipants.map((p, index) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 relative flex-shrink-0 w-full py-0.5 rounded-control bg-ink-muted"
                      style={{ zIndex: 100 - index }}
                    >
                      {/* Подложка: только аватар и имя, цвет #636569, радиус 100px */}
                      <div
                        className="flex items-center gap-3 w-fit max-w-full rounded-full py-1 pl-1 pr-4"
                        style={{ backgroundColor: '#636569' }}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-ui-strong"
                          style={{ backgroundColor: p.avatarColor ?? '#8ED0A7' }}
                        >
                          {(p.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-body-l truncate">{p.displayName}</span>
                      </div>
                      {/* Кнопки: скрыть из списка (только UI), затем Разрешить */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {onHideFromAwaiting && (
                          <IconButton
                            label="Скрыть из списка"
                            variant="ghostInverse"
                            onClick={() => onHideFromAwaiting(p.id)}
                          >
                            <HideFromListIcon width={20} height={20} stroke="#FF5F57" />
                          </IconButton>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghostInverse"
                          title={p.isAllowedPending ? 'Разрешить повторно' : undefined}
                          onClick={() => onAllow?.(p.id)}
                        >
                          {p.isAllowedPending ? 'Ожидает входа…' : 'Разрешить'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {displayAwaitingCount > 0 && (
                  <div className="flex justify-end flex-shrink-0">
                    <Button type="button" size="lg" variant="neutral" onClick={onAllowAll}>
                      Разрешить всем
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* На встрече */}
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 flex-shrink-0 relative z-10">
              <span className="text-white text-heading-lg">На встрече</span>
              <div className="flex items-center gap-1">
                <span className="text-white text-body-l tabular-nums">{displayOnMeetingCount}</span>
                <IconButton
                  label={isOnMeetingListExpanded ? 'Свернуть список' : 'Развернуть список'}
                  variant="ghostInverse"
                  aria-expanded={isOnMeetingListExpanded}
                  onClick={() => setIsOnMeetingListExpanded((v) => !v)}
                >
                  <ChevronUpDownIcon direction={isOnMeetingListExpanded ? 'up' : 'down'} />
                </IconButton>
              </div>
            </div>
            {isOnMeetingListExpanded && (
              <>
                <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 isolate">
                  {onMeetingParticipants.map((p, index) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 relative flex-shrink-0 w-full py-0.5 rounded-control bg-ink-muted"
                      style={{ zIndex: 100 - index }}
                    >
                      <div
                        className="flex items-center gap-3 w-fit max-w-full rounded-full py-1 pl-1 pr-4 flex-shrink-0 min-w-0"
                        style={{ backgroundColor: '#636569' }}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-ui-strong"
                          style={{ backgroundColor: p.avatarColor ?? '#8ED0A7' }}
                        >
                          {(p.displayName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-body-l truncate">{p.displayName}</span>
                      </div>
                      {(!p.isPsychologist || canAssignCoOrganizer) && (
                        <IconButton
                          ref={openParticipantMenuId === p.id ? menuTriggerRef : undefined}
                          label="Опции участника"
                          variant="ghostInverse"
                          size="md"
                          aria-expanded={openParticipantMenuId === p.id}
                          onClick={() => setOpenParticipantMenuId((id) => (id === p.id ? null : p.id))}
                        >
                          {openParticipantMenuId === p.id ? (
                            <EllipsisHorizontalActiveIcon width={30} height={30} />
                          ) : (
                            <EllipsisHorizontalIcon />
                          )}
                        </IconButton>
                      )}
                    </div>
                  ))}
                </div>
                {displayOnMeetingCount > 0 && (
                  <Button
                    type="button"
                    size="lg"
                    variant="neutral"
                    fullWidth
                    className="shrink-0"
                    onClick={() => {
                      onMuteAll?.();
                      if (onMuteAll) {
                        moderationFeedbackToast('Микрофон выключен всем участникам');
                      }
                    }}
                  >
                    Отключить звук всем
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {openParticipantMenuId &&
        menuPosition &&
        (() => {
          const p = onMeetingParticipants.find((pr) => pr.id === openParticipantMenuId);
          if (!p) return null;

          const closeMenu = () => setOpenParticipantMenuId(null);

          const participantLabel = (p.displayName || 'Участник').trim();

          const menuItems = [
            {
              label: 'Выключить звук',
              onClick: () => {
                closeMenu();
                onMuteParticipant?.(p.id).then((ok) => {
                  moderationFeedbackToast(
                    ok
                      ? `Микрофон успешно выключен: ${participantLabel}`
                      : `Не удалось подтвердить отключение микрофона: ${participantLabel}`,
                    ok ? 'success' : 'warning',
                  );
                });
              },
            },
            {
              label: 'Остановить видео',
              onClick: () => {
                closeMenu();
                onStopVideoParticipant?.(p.id).then((ok) => {
                  moderationFeedbackToast(
                    ok
                      ? `Видео успешно остановлено: ${participantLabel}`
                      : `Не удалось подтвердить остановку видео: ${participantLabel}`,
                    ok ? 'success' : 'warning',
                  );
                });
              },
            },
            ...(canAssignCoOrganizer
              ? [
                  {
                    label: 'Назначить соорганизатором',
                    onClick: () => {
                      onMakeCoOrganizer?.(p.id);
                      closeMenu();
                      if (onMakeCoOrganizer) {
                        moderationFeedbackToast(`Назначен соорганизатором: ${participantLabel}`);
                      }
                    },
                  },
                ]
              : []),
            {
              label: 'Удалить участника',
              onClick: () => {
                closeMenu();
                setParticipantIdToRemove(p.id);
              },
              variant: 'danger' as const,
            },
          ];

          return <ActionMenuPopup items={menuItems} position={menuPosition} contentRef={menuContentRef} />;
        })()}
      {/* Модальное окно подтверждения удаления участника */}
      <ConfirmDialog
        isOpen={!!participantIdToRemove}
        title="Удалить участника со встречи?"
        message="После удаления участник сможет присоединиться к этой встрече снова."
        confirmText="Удалить"
        onCancel={() => setParticipantIdToRemove(null)}
        onConfirm={() => {
          if (participantIdToRemove) onRemoveParticipant?.(participantIdToRemove);
          setParticipantIdToRemove(null);
        }}
      />
    </div>
  );
};
